<template>
  <div v-if="isVisible" class="edit-overlay">
    <div class="edit-overlay-backdrop" @click="close"></div>
    <div class="edit-overlay-content">
      <div class="edit-header">
        <h2>Attach MPID</h2>
        <div class="debug-info">
          <span>{{availableMpids.length}} items</span>
        </div>
        <button class="close-button" @click="close">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="form-container">
        <div v-if="loading" class="loading-overlay">
          <div class="loading-spinner"></div>
          <span>Loading available metering points...</span>
        </div>
        <div v-else-if="error" class="error-state">
          <div class="error-message">{{ error }}</div>
          <button class="retry-button" @click="fetchAvailableMpids">Retry</button>
        </div>
        <div v-else class="form-content">
          <!-- Search filters -->
          <div class="search-filters">
            <div class="search-input-container">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="search-icon">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                  type="text"
                  class="search-input"
                  v-model="searchQuery"
                  placeholder="Search by MPID or city..."
                  @input="handleSearchInput"
              >
            </div>
          </div>

          <!-- MPID selection table -->
          <div class="mpid-table-container">
            <div v-if="availableMpids.length === 0" class="no-results">
              No available metering points found matching your criteria.
            </div>
            <table v-else class="mpid-table">
              <thead>
              <tr>
                <th>Select</th>
                <th @click="sortBy('mpid')" class="sortable-header">
                  MPID
                  <span v-if="sortColumn === 'mpid'" class="sort-indicator">
                      {{ sortDirection === 'asc' ? '↑' : '↓' }}
                    </span>
                </th>
                <th @click="sortBy('city')" class="sortable-header">
                  City
                  <span v-if="sortColumn === 'city'" class="sort-indicator">
                      {{ sortDirection === 'asc' ? '↑' : '↓' }}
                    </span>
                </th>
                <th>Street</th>
                <th>Customer</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="mpid in availableMpids" :key="mpid.mpid" :class="{ 'selected-row': selectedMpid === mpid.mpid }">
                <td>
                  <div class="radio-container">
                    <input
                        type="radio"
                        :id="'mpid-' + mpid.mpid"
                        name="mpid-selection"
                        :value="mpid.mpid"
                        v-model="selectedMpid"
                    >
                    <label :for="'mpid-' + mpid.mpid" class="radio-label"></label>
                  </div>
                </td>
                <td>{{ mpid.mpid }}</td>
                <td>{{ mpid.city || 'N/A' }}</td>
                <td>{{ mpid.street || 'N/A' }}</td>
                <td>{{ mpid.customer_fname || 'N/A' }}</td>
              </tr>
              </tbody>
            </table>

            <!-- Pagination controls -->
            <div v-if="availableMpids.length > 0" class="pagination-controls">
              <div class="pagination-info">
                Showing {{ startItem }}-{{ endItem }} of {{ meta.total }} items
              </div>
              <div class="pagination-buttons">
                <button
                    class="pagination-button"
                    :disabled="meta.page === 1"
                    @click="changePage(meta.page - 1)"
                >
                  Previous
                </button>
                <div class="page-numbers">
                  <button
                      v-for="page in visiblePageNumbers"
                      :key="page"
                      :class="['page-number', { active: meta.page === page }]"
                      @click="changePage(page)"
                  >
                    {{ page }}
                  </button>
                </div>
                <button
                    class="pagination-button"
                    :disabled="meta.page === meta.totalPages"
                    @click="changePage(meta.page + 1)"
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <div v-if="attachError" class="attach-error-message">{{ attachError }}</div>
        </div>
      </div>

      <div class="edit-actions">
        <button class="action-button" @click="close" :disabled="loading">Cancel</button>
        <button
            class="action-button retry-button"
            @click="fetchAvailableMpids"
            :disabled="loading"
        >
          Refresh List
        </button>
        <button
            class="action-button submit-button"
            @click="attachMpid"
            :disabled="loading || !selectedMpid"
        >
          Attach
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeviceDetailsAttachMpidOverlay',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    deviceId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      availableMpids: [],
      selectedMpid: '',
      searchQuery: '',
      loading: false,
      error: '',
      attachError: '',
      // Sorting
      sortColumn: 'mpid',
      sortDirection: 'asc',
      // Pagination
      meta: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
      },
      searchTimeout: null
    };
  },
  computed: {
    startItem() {
      return this.availableMpids.length === 0 ? 0 : ((this.meta.page - 1) * this.meta.limit) + 1;
    },
    endItem() {
      return Math.min(this.startItem + this.availableMpids.length - 1, this.meta.total);
    },
    visiblePageNumbers() {
      const maxVisible = 5;
      const half = Math.floor(maxVisible / 2);
      let start = Math.max(1, this.meta.page - half);
      let end = Math.min(this.meta.totalPages, start + maxVisible - 1);

      if (end === this.meta.totalPages) {
        start = Math.max(1, end - maxVisible + 1);
      }

      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.resetFilters();
        this.$nextTick(() => {
          this.fetchAvailableMpids();
        });
      }
    }
  },

  mounted() {
    if (this.isVisible) {
      this.resetFilters();
      this.$nextTick(() => {
        this.fetchAvailableMpids();
      });
    }
  },
  methods: {
    resetFilters() {
      this.searchQuery = '';
      this.meta.page = 1;
      this.selectedMpid = '';
    },

    handleSearchInput() {
      // Debounce search to avoid too many API calls
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.meta.page = 1; // Reset to first page on new search
        this.fetchAvailableMpids();
      }, 300);
    },

    async fetchAvailableMpids() {
      this.loading = true;
      this.error = '';
      this.attachError = '';

      const apiBaseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000/api';

      // Build query parameters
      const params = new URLSearchParams({
        page: this.meta.page,
        limit: this.meta.limit,
        simple: 'false'  // Add this parameter as it seems to be in the API call
      });

      // Add search filters if they exist
      if (this.searchQuery) {
        // Check if the query looks like an MPID (alphanumeric without spaces)
        if (/^[A-Za-z0-9]+$/.test(this.searchQuery.trim())) {
          params.append('mpid', this.searchQuery.trim());
        } else {
          params.append('city', this.searchQuery.trim());
        }
      }

      const url = `${apiBaseUrl}/meteringpoints/available?${params.toString()}`;

      try {
        // Use the dedicated endpoint for available metering points
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Error fetching available metering points: ${response.statusText}`);
        }

        const data = await response.json();

        // Handle different API response formats
        if (data.items !== undefined && data.meta) {
          // Handle case where items can be null
          this.availableMpids = data.items || [];
          this.meta = data.meta;
        } else if (data.data && data.meta) {
          this.availableMpids = data.data;
          this.meta = data.meta;
        } else if (Array.isArray(data)) {
          this.availableMpids = data;
          this.meta = {
            page: 1,
            limit: data.length,
            total: data.length,
            totalPages: 1
          };
        } else {
          console.error('Unexpected API response format:', data);
          this.error = 'Unexpected API response format';
          this.availableMpids = [];
        }

        // Apply sorting
        if (this.availableMpids.length > 0) {
          this.sortBy(this.sortColumn, false);
        }
      } catch (err) {
        console.error('Error fetching available metering points:', err);
        this.error = 'Failed to load available metering points. Please try again.';
        this.availableMpids = [];
      } finally {
        this.loading = false;
      }
    },

    sortBy(column, toggleDirection = true) {
      // If clicking the same column, toggle direction
      if (this.sortColumn === column && toggleDirection) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        if (toggleDirection) {
          this.sortDirection = 'asc';
        }
      }

      if (!this.availableMpids || this.availableMpids.length === 0) {
        return;
      }

      try {
        // Sort the available list
        this.availableMpids.sort((a, b) => {
          const valueA = (a[column] || '').toString().toLowerCase();
          const valueB = (b[column] || '').toString().toLowerCase();

          if (this.sortDirection === 'asc') {
            return valueA.localeCompare(valueB);
          } else {
            return valueB.localeCompare(valueA);
          }
        });
      } catch (err) {
        // Silent error handling for sort operation
      }
    },

    changePage(page) {
      if (page >= 1 && page <= this.meta.totalPages) {
        this.meta.page = page;
        this.fetchAvailableMpids();
      }
    },

    async attachMpid() {
      if (!this.selectedMpid) {
        return;
      }

      this.loading = true;
      this.attachError = '';

      const apiBaseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000/api';
      const url = `${apiBaseUrl}/devices/${this.deviceId}/mpid/attach`;

      try {
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({ mpid: this.selectedMpid })
        });

        if (response.ok) {
          // Emit the event with the selected MPID to notify parent component
          this.$emit('mpid-attached', this.selectedMpid);
          this.close();
        } else {
          const resData = await response.json();

          const msg = resData.message ? resData.message.toLowerCase() : '';
          if (msg.includes('attached')) {
            this.attachError = 'Metering point id already has a device attached';
          } else if (msg.includes('not found')) {
            this.attachError = 'MPID not found';
          } else {
            this.attachError = resData.message || 'An error occurred while attaching the MPID';
          }
        }
      } catch (err) {
        this.attachError = 'Failed to attach MPID. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    close() {
      this.selectedMpid = '';
      this.searchQuery = '';
      this.meta.page = 1;
      this.error = '';
      this.attachError = '';
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Overlay */
.edit-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.edit-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(32,32,32,0.1) 0%, rgba(16,16,16,0.95) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease forwards;
}

.edit-overlay-content {
  position: relative;
  width: 800px;
  max-width: 90%;
  max-height: 90vh;
  border-radius: 8px;
  padding: 24px;
  color: white;
  background-color: rgba(50,50,50,0.2);
  backdrop-filter: blur(16px);
  box-shadow: 0 6px 16px rgba(0,0,0,0.3);
  overflow-y: auto;
  animation: slideIn 0.3s ease forwards;
  z-index: 1001;
}

/* Header */
.edit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  padding-bottom: 16px;
}

.debug-info {
  position: absolute;
  top: 10px;
  right: 50%;
  font-size: 11px;
  color: rgba(255,255,255,0.5);
}

.edit-header h2 {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
}

.close-button {
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.6);
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.close-button:hover {
  color: rgba(255,255,255,0.9);
}

/* Form container */
.form-container {
  position: relative;
  min-height: 300px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background-color: rgba(0,0,0,0.6);
  border-radius: 8px;
  gap: 16px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top: 3px solid rgba(255,255,255,0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error states */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.attach-error-message {
  color: #ff8a80;
  text-align: center;
  margin-top: 16px;
  font-size: 13px;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255,255,255,0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255,255,255,0.1);
}

/* Search filters */
.search-filters {
  margin-bottom: 16px;
}

.search-input-container {
  position: relative;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255,255,255,0.5);
}

.search-input {
  width: 100%;
  background-color: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 4px;
  color: white;
  padding: 10px 12px 10px 36px;
  font-size: 13px;
  transition: border-color 0.2s, background-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: rgba(79,195,247,0.5);
  background-color: rgba(79,195,247,0.05);
}

/* MPID Table */
.mpid-table-container {
  max-height: 350px;
  overflow-y: auto;
  margin-bottom: 16px;
  border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.1);
}

.mpid-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 12px;
}

.mpid-table th {
  background-color: rgba(0,0,0,0.2);
  padding: 12px 16px;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  position: sticky;
  top: 0;
  z-index: 1;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.sortable-header {
  cursor: pointer;
  user-select: none;
}

.sortable-header:hover {
  background-color: rgba(0,0,0,0.3);
}

.sort-indicator {
  margin-left: 4px;
  display: inline-block;
}

.mpid-table td {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}

.mpid-table tr:hover {
  background-color: rgba(255,255,255,0.05);
}

.selected-row {
  background-color: rgba(79,195,247,0.1);
}

.selected-row:hover {
  background-color: rgba(79,195,247,0.15);
}

.no-results {
  padding: 30px 16px;
  text-align: center;
  color: rgba(255,255,255,0.6);
  font-size: 13px;
}

/* Radio button styling */
.radio-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.radio-container input[type="radio"] {
  display: none;
}

.radio-label {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.3);
  position: relative;
  cursor: pointer;
}

.radio-label::after {
  content: "";
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: rgba(79,195,247,0.8);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0);
  transition: transform 0.2s ease-in-out;
}

input[type="radio"]:checked + .radio-label {
  border-color: rgba(79,195,247,0.8);
}

input[type="radio"]:checked + .radio-label::after {
  transform: translate(-50%, -50%) scale(1);
}

/* Pagination controls */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: rgba(0,0,0,0.1);
  border-top: 1px solid rgba(255,255,255,0.05);
}

.pagination-info {
  font-size: 11px;
  opacity: 0.7;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-button {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s;
}

.pagination-button:hover:not(:disabled) {
  background-color: rgba(255,255,255,0.1);
  opacity: 1;
}

.pagination-button:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-number {
  background: none;
  border: none;
  color: white;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
}

.page-number:hover:not(.active) {
  background-color: rgba(255,255,255,0.1);
}

.page-number.active {
  background-color: rgba(255,255,255,0.2);
  font-weight: 600;
}

/* Action buttons */
.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255,255,255,0.1);
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255,255,255,0.1);
}

.action-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.submit-button {
  background-color: rgba(79,195,247,0.2);
}

.submit-button:hover:not(:disabled) {
  background-color: rgba(79,195,247,0.3);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .edit-overlay-content {
    padding: 16px;
  }

  .mpid-table {
    font-size: 11px;
  }

  .mpid-table th,
  .mpid-table td {
    padding: 10px 8px;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 12px;
  }

  .pagination-info {
    text-align: center;
  }

  .pagination-buttons {
    justify-content: center;
  }
}
</style>